import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { IPatientDme } from "../../../../../../resources-vue/vue-interfaces/i-get-dme";
import { DME } from "../../../../../../resources-vue/vue-models/dme";
import { DMEService } from "../../../../../../services/dme.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { EnumsService } from "../../../../../../services/enums.service";
import { ConvertToPalEvent } from "../../../ConvertToPendingPal";
import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import eventBus from "../../../../../../utilities/eventBus";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import TableEmpty from "../../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import PatientVendor from "./PatientVendor.vue";
import DMETableRow from "./DMETableRow.vue";

export default defineComponent({
    components: {
        FormSection,
        TypeaheadInput,
        TableEmpty,
        PatientVendor,
        DMETableRow
    },
    props: {
        patientVendors: { type: Array as PropType<string[]>, default: [] },
        providerId: {
            type: String,
            default: ""
        },
        orderId: {
            type: String,
            default: null
        },
        patientStatus: {
            type: Number,
            default: null
        },
        isAttachOrderNotRequired: {
            type: Boolean,
            default: false
        },
        dmes: { type: Array as PropType<IPatientDme[]>, default: [] }
    },
    data() {
        return {
            columns: 5,
            vendorTypes: ["DME Company"],
            patientStatusEnums: [] as IEnumResponse[],
            dmeList: [] as DME[],
            selectionDme: null as ITypeaheadOptions,
            savetoAdd: null as IPatientDme,
            showDmeInputRow: null as boolean,
            _dmeService: null,
            _toastrService: null,
            _enumsService: null
        };
    },
    computed: {
        ...mapState(useConvertToPendingPal, ["accountId", "application"]),
        isEditAllowed() {
            let pendingStatus = this.patientStatusEnums.find((status) => status.name.toLowerCase().includes("pending"));
            return (
                !!this.orderId ||
                (pendingStatus && this.patientStatus == pendingStatus.value) ||
                this.isAttachOrderNotRequired
            );
        }
    },
    watch: {
        selectionDme(newValue: ITypeaheadOptions) {
            let dmeSelected: DME;
            if (newValue) {
                dmeSelected = this.dmeList.find((dme: DME) => dme.Id == newValue.value);
                if (dmeSelected) {
                    this.savetoAdd = {
                        id: null,
                        agencyDMEId: dmeSelected.Id,
                        name: dmeSelected.Name,
                        isInfusionPump: dmeSelected.IsInfusionPump,
                        startDate: "",
                        endDate: "",
                        isRelated: null,
                        notRelatedComments: null
                    };
                    this.showDmeInputRow = true;
                }
            } else {
                this.showDmeInputRow = false;
            }
        }
    },
    emits: ["update:patientVendors", "update:dmes"],
    created() {
        this._dmeService = Container.instance.get(DMEService);
        this._toastrService = Container.instance.get(ToastrService);
        this._enumsService = Container.instance.get(EnumsService);
    },
    async mounted() {
        let patientStatusEnums = await this._enumsService.getPatientStatus();
        this.patientStatusEnums = patientStatusEnums.getJson();
        if (!this.patientVendors?.length) {
            this.$emit("update:patientVendors", [null]);
        }
    },
    methods: {
        addNewVendor() {
            if (this.patientVendors?.length < 5) {
                const patientVendors = this.patientVendors.concat(null);
                this.$emit("update:patientVendors", patientVendors);
            }
        },
        updateDmes() {
            eventBus.emit(ConvertToPalEvent.RequestNoteId);
        },
        async getDMEList(filter: string) {
            try {
                if (filter.length > 0) {
                    let params = {
                        term: filter,
                        providerIds: [this.providerId],
                        application: this.application,
                        accountId: this.accountId
                    };
                    // Test this endpoint
                    // End point needs to be updated
                    this.dmeList = await this._dmeService.getDMEByTerm(params);
                    return this.displayPatientDME(this.dmeList);
                } else {
                    return [];
                }
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: "Error",
                    message: "There was a problem fetching DME List. Please try again."
                });
                throw e;
            }
        },
        displayPatientDME(getDME: DME[]): ITypeaheadOptions[] {
            return getDME.map((value: DME, index, number) => ({
                value: value.Id,
                name: value.Name
            }));
        },
        selectionDmeChanged(data: ITypeaheadOptions) {
            this.selectionDme = data;
        },
        saveNewDme(newDme: IPatientDme, oldDme: IPatientDme) {
            let existingDme = this.dmes.find(
                (dme) =>
                    dme.agencyDMEId == newDme.agencyDMEId &&
                    dme.startDate == newDme.startDate &&
                    dme.endDate == newDme.endDate
            );
            if (existingDme) {
                this._toastrService.info({
                    title: `${existingDme.name} already exists`,
                    message: `${existingDme.name} is already assigned to patient between ${existingDme.startDate} and ${existingDme.endDate}.`
                });
            } else {
                this.dmes.push(newDme);
            }
            this.cancelNewDme();
        },
        cancelNewDme() {
            this.savetoAdd = {
                id: null,
                agencyDMEId: null,
                name: null,
                isInfusionPump: null,
                startDate: null,
                endDate: null,
                isRelated: null,
                notRelatedComments: null
            };
            this.selectionDme = null;
            this.showDmeInputRow = false;
        },
        updateDme(newDme: IPatientDme, oldDme: IPatientDme) {
            let dmeIndex = this.dmes.findIndex(
                (dme) =>
                    dme.agencyDMEId == oldDme.agencyDMEId &&
                    dme.startDate == oldDme.startDate &&
                    dme.endDate == oldDme.endDate
            );
            if (dmeIndex > -1) {
                this.dmes[dmeIndex] = newDme;
            }
        },
        deleteDmeFromList(deleteDme: IPatientDme) {
            let dmes = this.dmes.filter(
                (dme) =>
                    !(
                        dme.agencyDMEId == deleteDme.agencyDMEId &&
                        dme.startDate == deleteDme.startDate &&
                        dme.endDate == deleteDme.endDate
                    )
            );
            this.$emit("update:dmes", dmes);
        }
    }
});
